import { isEmail } from 'components/forms/inputs/EmailListInput';
import { useState } from 'react';
import { Path, PathValue, UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import { Employee } from 'types/employees';
import { EmailFields, InvoiceEmailFormBase } from 'types/invoices';

interface UseEmailManagementProps<T extends InvoiceEmailFormBase> {
    setValue: UseFormSetValue<T>;
    getValues: UseFormGetValues<T>;
    employees: Employee[];
}

// EmailFields literals ('to' | 'cc' | 'bcc' | 'replyTo') are known to resolve
// to string[] on T (via T extends InvoiceEmailFormBase), but RHF's Path<T> /
// PathValue<T, Path<T>> don't narrow this through a generic — so bridge with
// concrete-typed cast helpers rather than `any`.
type EmailListValue<T extends InvoiceEmailFormBase> = PathValue<T, Path<T>>;

export const useEmailManagement = <T extends InvoiceEmailFormBase>({
    setValue,
    getValues,
    employees,
}: UseEmailManagementProps<T>) => {
    const [employeePickerOpen, setEmployeePickerOpen] = useState(false);
    const [currentFieldForPicker, setCurrentFieldForPicker] = useState<EmailFields | null>(null);

    const fieldPath = (field: EmailFields) => field as Path<T>;
    const asListValue = (emails: string[]) => emails as EmailListValue<T>;
    const readEmails = (field: EmailFields) => (getValues(fieldPath(field)) ?? []) as string[];

    const removeEmail = (val: string, field: EmailFields) => {
        const updatedValues = readEmails(field).filter((va) => va !== val);
        setValue(fieldPath(field), asListValue(updatedValues), { shouldDirty: true, shouldValidate: true });
    };

    const addEmail = (val: string, field: EmailFields) => {
        const currentValues = readEmails(field);
        if (isEmail(val)) {
            currentValues.push(val);
        }
        setValue(fieldPath(field), asListValue(currentValues), { shouldDirty: true, shouldValidate: true });
    };

    const openEmployeePicker = (field: EmailFields) => {
        setCurrentFieldForPicker(field);
        setEmployeePickerOpen(true);
    };

    const handleEmployeePickerConfirm = (employeeIds: string[]) => {
        if (!currentFieldForPicker) return;

        const selectedEmployees = employees.filter((emp) => employeeIds.includes(emp.id.toString()));
        const emails = selectedEmployees
            .map((emp) => emp.workEmailAddress)
            .filter((email): email is string => !!email && !!isEmail(email));

        const existingEmails = readEmails(currentFieldForPicker);
        const newEmails = emails.filter((email) => !existingEmails.includes(email));

        if (newEmails.length > 0) {
            setValue(fieldPath(currentFieldForPicker), asListValue([...existingEmails, ...newEmails]), {
                shouldDirty: true,
                shouldValidate: true,
            });
        }
    };

    return {
        employeePickerOpen,
        setEmployeePickerOpen,
        currentFieldForPicker,
        removeEmail,
        addEmail,
        openEmployeePicker,
        handleEmployeePickerConfirm,
    };
};
